import { useState } from "react";
import { toast } from "sonner";
import { CalendarClock, CalendarPlus, Loader2 } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { formatWib, toWibInput, fromWibInput, addMonthsIso } from "@/lib/datetime";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel,
} from "@/components/ui/alert-dialog";

export const STATUS_LABEL = {
  aktif: "Aktif",
  nonaktif: "Nonaktif",
  kedaluwarsa: "Kedaluwarsa",
  belum_dimulai: "Belum Dimulai",
};

const STATUS_CLASS = {
  aktif: "border-primary/40 text-primary",
  nonaktif: "border-destructive/40 text-destructive",
  kedaluwarsa: "border-destructive/50 bg-destructive/10 text-destructive",
  belum_dimulai: "border-sky-500/50 text-sky-600 dark:text-sky-400",
};

export function StatusBadges({ u }) {
  const status = u.status || (u.disabled ? "nonaktif" : "aktif");
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <Badge variant="outline" className={`rounded-md ${STATUS_CLASS[status] || ""}`} data-testid={`admin-status-${u.user_id}`}>
        {STATUS_LABEL[status] || status}
      </Badge>
      {u.locked && (
        <Badge variant="outline" className="rounded-md border-amber-500/50 text-amber-600 dark:text-amber-400" data-testid={`admin-locked-${u.user_id}`}>Terkunci</Badge>
      )}
    </div>
  );
}

export function RemainingDays({ u }) {
  if (u.role === "admin") return <span className="text-xs text-muted-foreground">Tanpa batas (admin)</span>;
  if (u.remaining_days == null) return <span className="text-xs text-muted-foreground" data-testid={`admin-remaining-${u.user_id}`}>Tanpa batas</span>;
  if (u.remaining_days === 0) return <span className="text-sm font-semibold text-destructive" data-testid={`admin-remaining-${u.user_id}`}>Habis</span>;
  const warn = u.remaining_days <= 7 ? "text-destructive" : u.remaining_days <= 30 ? "text-amber-600 dark:text-amber-400" : "";
  return <span className={`font-mono text-sm font-semibold ${warn}`} data-testid={`admin-remaining-${u.user_id}`}>{u.remaining_days} hari</span>;
}

/** Shared activation / expiration / note inputs (values are WIB datetime-local strings). */
export function AccessFields({ value, onChange, showNote = true, testPrefix = "admin-access" }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  const plus12 = () => {
    const baseIso = fromWibInput(value.start) || new Date().toISOString();
    set("expires", toWibInput(addMonthsIso(baseIso, 12)));
  };
  return (
    <div className="space-y-3 rounded-xl border border-border bg-secondary/30 p-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Masa Aktif Akun (WIB, UTC+7)</p>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1.5"><Label>Mulai Aktif</Label>
          <Input type="datetime-local" value={value.start} onChange={(e) => set("start", e.target.value)} className="rounded-xl" data-testid={`${testPrefix}-start-input`} /></div>
        <div className="space-y-1.5"><Label>Berakhir (Kedaluwarsa)</Label>
          <Input type="datetime-local" value={value.expires} onChange={(e) => set("expires", e.target.value)} className="rounded-xl" data-testid={`${testPrefix}-expires-input`} /></div>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button type="button" variant="secondary" size="sm" className="rounded-lg" onClick={plus12} data-testid={`${testPrefix}-plus12-button`}>Berakhir = Mulai + 12 bulan</Button>
        <Button type="button" variant="ghost" size="sm" className="rounded-lg" onClick={() => set("expires", "")} data-testid={`${testPrefix}-clear-expires-button`}>Tanpa batas waktu</Button>
      </div>
      <p className="text-xs text-muted-foreground">Kosongkan tanggal berakhir untuk akses tanpa batas. Setelah tanggal berakhir, akun otomatis tidak bisa mengakses aplikasi (data bisnis tetap aman).</p>
      {showNote && (
        <div className="space-y-1.5"><Label>Catatan Admin (opsional)</Label>
          <Textarea value={value.note} maxLength={500} onChange={(e) => set("note", e.target.value)} className="rounded-xl" rows={2} placeholder="mis. Paket tahunan, dibayar via transfer" data-testid={`${testPrefix}-note-input`} /></div>
      )}
    </div>
  );
}

/** Converts AccessFields state to API payload; returns { error } on invalid input. */
export function accessPayload(v) {
  const start = fromWibInput(v.start);
  const expires = fromWibInput(v.expires);
  if (start === undefined || expires === undefined) return { error: "Format tanggal/waktu tidak valid." };
  if (start && expires && new Date(expires) <= new Date(start)) return { error: "Tanggal kedaluwarsa harus setelah tanggal aktivasi." };
  return { access_start_at: start, access_expires_at: expires, admin_note: (v.note || "").trim() || null };
}

export function EditAccessDialog({ user, onDone }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [val, setVal] = useState({ start: "", expires: "", note: "" });

  const openDialog = () => {
    setVal({ start: toWibInput(user.access_start_at), expires: toWibInput(user.access_expires_at), note: user.admin_note || "" });
    setOpen(true);
  };
  const submit = async () => {
    const p = accessPayload(val);
    if (p.error) return toast.error(p.error);
    setSaving(true);
    try {
      await api.put(`/admin/users/${user.user_id}/access`, p);
      toast.success("Masa aktif akun disimpan.");
      setOpen(false); onDone();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <>
      <button onClick={openDialog} title="Atur masa aktif" aria-label="Atur masa aktif" className="text-muted-foreground hover:text-primary" data-testid={`admin-edit-access-${user.user_id}`}>
        <CalendarClock className="h-4 w-4" />
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Atur Masa Aktif — {user.name}</DialogTitle>
            <DialogDescription>{user.email}</DialogDescription>
          </DialogHeader>
          <AccessFields value={val} onChange={setVal} testPrefix="admin-edit-access" />
          <p className="text-xs text-muted-foreground">
            Perpanjangan: {user.renewal_count || 0} kali{user.last_renewed_at ? ` · terakhir ${formatWib(user.last_renewed_at)}` : ""}
          </p>
          <DialogFooter>
            <Button onClick={submit} disabled={saving} className="rounded-xl" data-testid="admin-edit-access-submit-button">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Simpan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function RenewDialog({ user, onDone }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const now = new Date();
  const exp = user.access_expires_at ? new Date(user.access_expires_at) : null;
  const noExpiry = !exp;
  const expired = exp && exp <= now;
  const base = exp && !expired ? exp.toISOString() : now.toISOString();
  const preview = addMonthsIso(base, 12);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/users/${user.user_id}/renew`, { confirm_from_now: noExpiry });
      toast.success(`Akun "${user.email}" diperpanjang hingga ${formatWib(data.access_expires_at)}.`);
      setOpen(false); onDone();
    } catch (err) {
      toast.error(`Perpanjangan gagal: ${apiError(err.response?.data?.detail)}`);
    } finally { setBusy(false); }
  };

  return (
    <>
      <Button variant="outline" size="sm" className="h-8 rounded-lg px-2 text-xs whitespace-nowrap" onClick={() => setOpen(true)} data-testid={`admin-renew-${user.user_id}`}>
        <CalendarPlus className="mr-1 h-3.5 w-3.5" /> Perpanjang 12 Bulan
      </Button>
      <AlertDialog open={open} onOpenChange={(o) => !busy && setOpen(o)}>
        <AlertDialogContent className="rounded-2xl" data-testid="admin-renew-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Perpanjang 12 Bulan?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-muted-foreground">
                <p>Akun <b className="text-foreground">{user.name}</b> ({user.email}).</p>
                {noExpiry && (
                  <p className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-2 text-amber-700 dark:text-amber-300" data-testid="admin-renew-noexpiry-warning">
                    Akun ini <b>belum memiliki tanggal kedaluwarsa</b> (akses tanpa batas). Jika dilanjutkan, masa aktif dihitung <b>12 bulan dari sekarang</b>. Untuk tanggal lain, gunakan “Atur Masa Aktif”.
                  </p>
                )}
                {expired && <p>Akun sudah kedaluwarsa sejak {formatWib(user.access_expires_at)}. Perpanjangan dihitung dari sekarang.</p>}
                {exp && !expired && <p>Masa aktif saat ini berakhir {formatWib(user.access_expires_at)}. Perpanjangan dihitung dari tanggal tersebut.</p>}
                <p>Berakhir baru (perkiraan): <b className="text-foreground" data-testid="admin-renew-preview">{formatWib(preview)}</b></p>
                <p className="text-xs">Lakukan hanya setelah pembayaran terverifikasi. Data bisnis pelanggan tidak berubah.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl" disabled={busy}>Batal</AlertDialogCancel>
            <Button className="rounded-xl" onClick={submit} disabled={busy} data-testid="admin-renew-confirm-button">
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Ya, Perpanjang
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
